'use client';

import { c as _c } from "react/compiler-runtime";
import { m } from 'motion/react';
import Image from 'next/image';
import { useState } from 'react';
import { useIsClientMounted } from '@/hooks/useIsClientMounted';
import { AnimatedGroup } from '@/components/ui/animated-group';
import { ProgressiveBlur } from '@/components/ui/progressive-blur';
import { useAnalytics } from '@/hooks/useAnalytics';

// calculate imageNumbers outside the component so the reference is stable
const imageNumbers = Array.from({
  length: 16
}, (_, i) => i + 1).filter(num => num !== 8);

// Placeholder colors for missing images
const placeholderColors = ['bg-blue-500', 'bg-red-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500', 'bg-cyan-500', 'bg-lime-500', 'bg-emerald-500', 'bg-sky-500', 'bg-violet-500', 'bg-fuchsia-500', 'bg-rose-500'];

// Low quality placeholder blur data URLs (simple colored squares)
const getPlaceholderDataURL = (colorIndex: number) => {
  const colors = ['rgb(59,130,246)', 'rgb(239,68,68)', 'rgb(34,197,94)', 'rgb(234,179,8)', 'rgb(168,85,247)', 'rgb(236,72,153)', 'rgb(99,102,241)', 'rgb(20,184,166)', 'rgb(249,115,22)', 'rgb(6,182,212)', 'rgb(132,204,22)', 'rgb(16,185,129)', 'rgb(14,165,233)', 'rgb(139,92,246)', 'rgb(232,121,249)', 'rgb(244,63,94)'];
  const color = colors[colorIndex % colors.length];
  return `data:image/svg+xml;base64,${Buffer.from(`<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" fill="${color}" /></svg>`).toString('base64')}`;
};

// Image metadata with titles and captions
const imageMetadata = {
  1: {
    title: 'Cosmic Harmony',
    caption: 'Abstract exploration of space and time'
  },
  2: {
    title: 'Urban Reflections',
    caption: 'City life through a fragmented lens'
  },
  3: {
    title: 'Natural Patterns',
    caption: 'Organic forms in perfect symmetry'
  },
  4: {
    title: 'Digital Dreams',
    caption: 'Where technology meets imagination'
  },
  5: {
    title: 'Chromatic Waves',
    caption: 'A symphony of color and movement'
  },
  6: {
    title: 'Geometric Precision',
    caption: 'Mathematical beauty in visual form'
  },
  7: {
    title: 'Ethereal Light',
    caption: 'Capturing the essence of illumination'
  },
  9: {
    title: 'Textural Landscape',
    caption: 'Tactile dimensions of visual space'
  },
  10: {
    title: 'Minimalist Expression',
    caption: 'Saying more with less'
  },
  11: {
    title: 'Fluid Dynamics',
    caption: 'The art of motion captured'
  },
  12: {
    title: 'Architectural Vision',
    caption: 'Reimagining structural possibilities'
  },
  13: {
    title: 'Quantum Fragments',
    caption: 'Visualizing the invisible world'
  },
  14: {
    title: 'Emotional Resonance',
    caption: 'Art that speaks to the soul'
  },
  15: {
    title: 'Temporal Shift',
    caption: 'Exploring the concept of time'
  },
  16: {
    title: 'Harmonic Contrast',
    caption: 'Balance between opposing elements'
  }
};
export default function CraftGallery() {
  const $ = _c(13);
  const mounted = useIsClientMounted();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [imageErrors, setImageErrors] = useState(t0);
  const [hoveredImage, setHoveredImage] = useState(null);
  const {
    track
  } = useAnalytics();
  if (!mounted) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="h-96 w-full animate-pulse bg-neutral-200 dark:bg-neutral-800" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = num => {
      setImageErrors(prev => ({
        ...prev,
        [num]: true
      }));
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleImageError = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      hidden: t2,
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05
        }
      }
    };
    t4 = {
      opacity: 0,
      y: 40,
      filter: "blur(4px)"
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      container: t3,
      item: {
        hidden: t4,
        visible: {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          transition: {
            duration: 1.2,
            type: "spring",
            bounce: 0.3
          }
        }
      }
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== hoveredImage || $[8] !== imageErrors || $[9] !== track) {
    t6 = imageNumbers.map(num_0 => {
      const isLarge = num_0 % 5 === 0 || num_0 % 7 === 0;
      const isHovered = hoveredImage === num_0;
      const metadata = imageMetadata[num_0 as keyof typeof imageMetadata] || {
        title: `Artwork ${num_0}`,
        caption: "Untitled piece"
      };
      return <div key={num_0} className="relative overflow-hidden rounded-lg" style={{
        gridRow: num_0 % 5 === 0 ? "span 2" : "auto",
        gridColumn: num_0 % 7 === 0 ? "span 2" : "auto"
      }} onMouseEnter={() => setHoveredImage(num_0)} onMouseLeave={() => setHoveredImage(null)} onClick={() => {
        track("content_engagement", {
          type: "craft_gallery",
          details: `image_${num_0}_${metadata.title}`
        });
      }} onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          track("content_engagement", {
            type: "craft_gallery",
            details: `image_${num_0}_${metadata.title}`
          });
        }
      }} role="button" tabIndex={0} aria-label={metadata.title}>{imageErrors[num_0] ? <div className={`flex h-64 items-center justify-center ${placeholderColors[num_0 - 1]}`}><span className="text-xl font-bold text-white">{num_0}</span></div> : <div className="relative h-auto w-full"><Image src={`/images/craft/${num_0}.jpg`} alt={metadata.title} className="h-auto w-full rounded-lg" width={1200} height={isLarge ? 800 : 600} style={{
            maxHeight: isLarge ? "500px" : "300px",
            objectFit: "contain"
          }} sizes={isLarge ? "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" : "(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"} priority={false} placeholder="blur" blurDataURL={getPlaceholderDataURL(num_0 - 1)} onError={() => handleImageError(num_0)} /><ProgressiveBlur className="pointer-events-none absolute bottom-0 left-0 h-[75%] w-full" blurIntensity={0.5} animate={isHovered ? "visible" : "hidden"} variants={{
            hidden: {
              opacity: 0
            },
            visible: {
              opacity: 1
            }
          }} transition={{
            duration: 0.2,
            ease: "easeOut"
          }} /><m.div className="scrim-to-t absolute inset-0 [--scrim-color:rgb(0_0_0/0.8)]" animate={isHovered ? "visible" : "hidden"} variants={{
            hidden: {
              opacity: 0
            },
            visible: {
              opacity: 1
            }
          }} transition={{
            duration: 0.2,
            ease: "easeOut"
          }} /><m.div className="absolute bottom-0 left-0 w-full" animate={isHovered ? "visible" : "hidden"} variants={{
            hidden: {
              opacity: 0,
              y: 10
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} transition={{
            duration: 0.2,
            ease: "easeOut"
          }}><div className="flex flex-col items-start gap-0.5 px-5 py-4"><p className="text-base font-semibold text-white">{metadata.title}</p><span className="text-sm text-zinc-200">{metadata.caption}</span></div></m.div></div>}</div>;
    });
    $[7] = hoveredImage;
    $[8] = imageErrors;
    $[9] = track;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <AnimatedGroup className="grid grid-cols-2 gap-4 p-8 md:grid-cols-3 lg:grid-cols-4" variants={t5}>{t6}</AnimatedGroup>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}